/**
 * Pure functions for deriving best/average/worst/spread from raw attempt
 * values. Nothing here is ever stored — attempts (test_attempts) are always
 * the source of truth; these are computed at read time, in the client today
 * and potentially in a view/RPC later, without changing what's stored.
 */

function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * @param {Array<number>} values - raw attempt results (already numeric)
 * @param {'lower'|'higher'} betterDirection - from test_types.better_direction
 * @returns {{best:number|null, worst:number|null, average:number|null, spread:number|null, count:number}}
 */
export function computeAttemptStats(values, betterDirection) {
  const valid = (values || []).filter(v => typeof v === 'number' && !Number.isNaN(v));
  if (valid.length === 0) {
    return { best: null, worst: null, average: null, spread: null, count: 0 };
  }

  const sum = valid.reduce((a, b) => a + b, 0);
  const average = sum / valid.length;
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const lowerIsBetter = betterDirection === 'lower';

  return {
    best: round2(lowerIsBetter ? min : max),
    worst: round2(lowerIsBetter ? max : min),
    average: round2(average),
    spread: round2(max - min),
    count: valid.length,
  };
}

/** Coerces a raw attempt input (string from a text field, or number) to a
 * valid positive number, or null if it's blank/invalid. Used both for live
 * UI stats and before submitting to Supabase (which also enforces
 * result_value > 0 via a CHECK constraint — this is the friendly client-side
 * mirror of that rule, not a replacement for it). */
export function parseAttemptValue(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = Number(raw);
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
}

/**
 * Phase C.7 — the ONE Personal Best derivation, across an athlete's
 * ENTIRE history (every session, every attempt) — never per-date, never
 * per-session (computeAttemptStats() above already covers those). Built
 * alongside computeAttemptStats() in this same file rather than a
 * second module, since it's the same underlying rule
 * (test_types.better_direction decides "best," never a hardcoded
 * Math.min()) applied across a wider scope.
 *
 * Returns one entry per test type that has at least one valid attempt —
 * a test with zero attempts is simply absent from the result, never a
 * fabricated null placeholder; callers render "no history yet" for
 * whichever test_types aren't present.
 *
 * Each entry also carries WHICH attempt achieved it (date/session/
 * attempt number) — "PB: 5.31 sec, Aug 24 2026" needs the date, not
 * just the value. On an exact tie, the first occurrence encountered in
 * `sessions`' own given order wins (this app lists sessions newest-
 * first, so a tie keeps the most recent occurrence) — ties are rare and
 * the two attempts are equally "the PB" either way; this is a stable,
 * documented tie-break, not an arbitrary one.
 *
 * Test Result Correction/Invalidation (055) — an attempt with
 * status === 'invalidated' is skipped entirely here, regardless of how
 * good its value looks; a bad or corrected-away result must never win a
 * PB. This is the ONE place that filter is applied — every consumer
 * (DrillHistory's PB strip, AthleteCard via its caller, both Testing
 * Stations' live "New PB" feedback) calls this same function, so
 * nothing else needs its own copy of this rule. A replacement attempt
 * is an ordinary 'valid' row and competes normally, no special casing.
 *
 * @param {Array} sessions - testing_sessions rows, each with a nested
 *   test_attempts array (the exact shape listTestingSessions() returns).
 * @param {Array} testTypes - test_types rows (needs .key/.better_direction).
 * @returns {Array<{testType, value, date, sessionId, attemptNumber}>}
 */
export function derivePersonalBests(sessions, testTypes) {
  const results = [];
  for (const testType of testTypes || []) {
    let best = null;
    for (const session of sessions || []) {
      for (const attempt of session.test_attempts || []) {
        if (attempt.test_type !== testType.key) continue;
        if (attempt.status === 'invalidated') continue;
        const value = attempt.result_value;
        if (typeof value !== 'number' || Number.isNaN(value)) continue;
        const isBetter = best === null || (testType.better_direction === 'lower' ? value < best.value : value > best.value);
        if (isBetter) {
          best = { value, date: session.test_date, sessionId: session.id, attemptNumber: attempt.attempt_number };
        }
      }
    }
    if (best) results.push({ testType, ...best });
  }
  return results;
}

/**
 * Reconstructs the { test_type: [value_or_null, ...] } shape the atomic
 * session-write RPCs expect from an existing session's nested
 * test_attempts — preserving each attempt's attempt_number as its array
 * index (1-based -> 0-based), with null filling any gap.
 *
 * This matters more than it looks: a naive "sort the attempts and re-index
 * from 0" would silently renumber every attempt after a gap (e.g. if
 * attempt 2 had been removed, attempt 3 would become "attempt 2" on the
 * next save) — including ones the user never touched. Both the full
 * edit-session form and inline single-attempt editing in Testing History
 * round-trip through this, so a full session save can never accidentally
 * reshuffle attempts that weren't part of the edit.
 */
export function buildAttemptsByTypeFromSession(session, testTypeKeys = []) {
  const byType = {};
  testTypeKeys.forEach(key => { byType[key] = []; });
  (session?.test_attempts || []).forEach(a => {
    if (!byType[a.test_type]) byType[a.test_type] = [];
    const arr = byType[a.test_type];
    while (arr.length < a.attempt_number) arr.push(null);
    arr[a.attempt_number - 1] = a.result_value;
  });
  return byType;
}

/**
 * Testing History display fix — "Attempt #N" as shown to a guardian/
 * coach must mean "the athlete's chronological attempt for this TEST on
 * this DATE, across every underlying testing_sessions row," never the
 * STORED test_attempts.attempt_number, which is scoped to one session +
 * test_type (005) and legitimately restarts at 1 in every session. Two
 * (or more) sessions for the same athlete/test/date — e.g. a Team
 * Testing session and a later Personal Testing session, or two separate
 * Personal Testing station visits — each independently produce their
 * own #1, #2, ... which is completely correct in storage but reads as
 * duplicate/out-of-order attempt numbers if the raw stored value is
 * rendered directly.
 *
 * This function NEVER changes the stored attempt_number — it derives a
 * separate, display-only `displayNumber` field alongside the original
 * `session`/`attempt` objects, using each attempt's own `created_at`
 * (a real, per-row timestamptz set once at INSERT time, 005) as the
 * chronology — the one durable ordering signal that stays correct
 * across session boundaries, unlike attempt_number itself. Ties (same
 * millisecond, effectively never in practice) fall back to the stored
 * attempt_number for a stable, deterministic order.
 *
 * @param {Array} sessions - testing_sessions rows, each with a nested
 *   test_attempts array (the exact shape listTestingSessions() returns).
 * @param {string} testTypeKey - which test to flatten/number.
 * @returns {Array<{session, attempt, displayNumber}>} sorted by
 *   test_date ascending, then chronologically within each date;
 *   displayNumber restarts at 1 for every new date.
 */
export function deriveChronologicalAttempts(sessions, testTypeKey) {
  const out = [];
  (sessions || []).forEach(session => {
    (session.test_attempts || [])
      .filter(a => a.test_type === testTypeKey)
      .forEach(attempt => out.push({ session, attempt }));
  });

  out.sort((a, b) => {
    const d = a.session.test_date.localeCompare(b.session.test_date);
    if (d !== 0) return d;
    const c = (a.attempt.created_at || '').localeCompare(b.attempt.created_at || '');
    if (c !== 0) return c;
    return a.attempt.attempt_number - b.attempt.attempt_number;
  });

  let runningDate = null;
  let displayNumber = 0;
  return out.map(row => {
    if (row.session.test_date !== runningDate) {
      runningDate = row.session.test_date;
      displayNumber = 0;
    }
    displayNumber += 1;
    return { ...row, displayNumber };
  });
}
